import * as React from 'react';
import { Check, Info } from 'lucide-react';
import { Select, type SelectOption, type SelectOptionGroup } from './select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../data-display/tooltip';
import { cn } from '../../utils';

/**
 * One AVS compensation fund as served by the backend directory query
 * (`CompensationFundOption`), already projected to the user's language.
 * Declared structurally so the design system stays backend-free.
 */
export interface CompensationFundOption {
  /** Swissdec caisse number `NNN.NNN`. */
  number: string;
  kind: 'cantonal' | 'professional';
  canton?: string;
  title: string;
  name: string;
  postalCode: string;
  city: string;
}

export interface CompensationFundSelectLabels {
  placeholder: string;
  search: string;
  noResults: string;
  cantonalGroup: string;
  professionalGroup: string;
  unlistedGroup: string;
  /** Suffix of a stored value missing from the directory. */
  unlisted: string;
  missingHint: string;
}

const DEFAULT_LABELS: CompensationFundSelectLabels = {
  placeholder: 'Select a compensation fund',
  search: 'Search by name, number, canton or city…',
  noResults: 'No compensation fund found.',
  cantonalGroup: 'Cantonal compensation funds',
  professionalGroup: 'Professional compensation funds',
  unlistedGroup: 'Current value',
  unlisted: 'not listed',
  missingHint:
    'Every official compensation fund is listed. If yours is missing, contact support.',
};

export interface CompensationFundSelectProps {
  funds: CompensationFundOption[];
  /** Selected fund number. */
  value?: string;
  onChange?: (number: string) => void;
  /** Collation locale for the professional list. Defaults to "fr". */
  locale?: string;
  /** Canton whose cantonal fund is listed first (the company's canton). */
  preferredCanton?: string;
  /**
   * Stored value absent from `funds` (legacy free-text entry, federal fund…).
   * Shown as its own option so the value is never silently dropped.
   */
  unlistedFund?: { number: string; name: string };
  labels?: Partial<CompensationFundSelectLabels>;
  /** Replaces the tooltip text, e.g. to add a link to the support page. */
  renderMissingHint?: () => React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  id?: string;
  className?: string;
  disablePortal?: boolean;
}

/** Lower-case, accent-free, whitespace-collapsed text for searching. */
export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** `106.005` → `106.5`, `019.000` → `19`: the form printed on ahv-iv.ch. */
export function shortCompensationFundNumber(number: string): string {
  const match = /^(\d{3})\.(\d{3})$/.exec(number);
  if (!match) return number;
  const caisse = String(Number(match[1]));
  const agency = Number(match[2]);
  return agency === 0 ? caisse : `${caisse}.${agency}`;
}

/** Every query word must appear in the number, title, name, canton, postcode or city. */
export function matchesCompensationFund(
  fund: Pick<CompensationFundOption, 'number' | 'name'> &
    Partial<CompensationFundOption>,
  query: string,
): boolean {
  const words = normalizeSearchText(query).split(' ').filter(Boolean);
  if (words.length === 0) return true;
  const haystack = normalizeSearchText(
    [
      fund.number,
      shortCompensationFundNumber(fund.number),
      fund.title,
      fund.name,
      fund.canton,
      fund.postalCode,
      fund.city,
    ]
      .filter(Boolean)
      .join(' '),
  );
  return words.every((word) => haystack.includes(word));
}

export function compensationFundLabel(fund: {
  number: string;
  name: string;
}): string {
  return `${fund.name} (${fund.number})`;
}

/**
 * Cantonal funds first (preferred canton on top, then by canton), then
 * professional funds sorted by name in `locale`.
 */
export function groupCompensationFunds(
  funds: CompensationFundOption[],
  {
    locale = 'fr',
    preferredCanton,
    labels = DEFAULT_LABELS,
    unlistedFund,
  }: {
    locale?: string;
    preferredCanton?: string;
    labels?: Pick<
      CompensationFundSelectLabels,
      'cantonalGroup' | 'professionalGroup' | 'unlistedGroup' | 'unlisted'
    >;
    unlistedFund?: { number: string; name: string };
  } = {},
): SelectOptionGroup[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base' });
  const toOption = (fund: { number: string; name: string }): SelectOption => ({
    value: fund.number,
    label: compensationFundLabel(fund),
  });

  const cantonal = funds
    .filter((fund) => fund.kind === 'cantonal')
    .sort((a, b) => {
      const preferredA = a.canton === preferredCanton ? 0 : 1;
      const preferredB = b.canton === preferredCanton ? 0 : 1;
      return (
        preferredA - preferredB ||
        collator.compare(a.canton ?? a.title, b.canton ?? b.title) ||
        a.number.localeCompare(b.number)
      );
    });
  const professional = funds
    .filter((fund) => fund.kind === 'professional')
    .sort(
      (a, b) =>
        collator.compare(a.name, b.name) || a.number.localeCompare(b.number),
    );

  const groups: SelectOptionGroup[] = [];
  if (unlistedFund && !funds.some((f) => f.number === unlistedFund.number)) {
    groups.push({
      label: labels.unlistedGroup,
      options: [
        {
          value: unlistedFund.number,
          label: `${compensationFundLabel(unlistedFund)} — ${labels.unlisted}`,
        },
      ],
    });
  }
  if (cantonal.length > 0) {
    groups.push({
      label: labels.cantonalGroup,
      options: cantonal.map(toOption),
    });
  }
  if (professional.length > 0) {
    groups.push({
      label: labels.professionalGroup,
      options: professional.map(toOption),
    });
  }
  return groups;
}

const CompensationFundSelect = React.forwardRef<
  HTMLButtonElement,
  CompensationFundSelectProps
>(
  (
    {
      funds,
      value,
      onChange,
      locale = 'fr',
      preferredCanton,
      unlistedFund,
      labels: labelOverrides,
      renderMissingHint,
      disabled = false,
      error = false,
      id,
      className,
      disablePortal = false,
    },
    ref,
  ) => {
    const labels = React.useMemo(
      () => ({ ...DEFAULT_LABELS, ...labelOverrides }),
      [labelOverrides],
    );

    const byNumber = React.useMemo(
      () => new Map(funds.map((fund) => [fund.number, fund])),
      [funds],
    );

    const groups = React.useMemo(
      () =>
        groupCompensationFunds(funds, {
          locale,
          preferredCanton,
          labels,
          unlistedFund,
        }),
      [funds, locale, preferredCanton, labels, unlistedFund],
    );

    const filterOption = React.useCallback(
      (option: SelectOption, query: string) => {
        const fund = byNumber.get(option.value);
        return fund
          ? matchesCompensationFund(fund, query)
          : matchesCompensationFund(
              { number: option.value, name: option.label },
              query,
            );
      },
      [byNumber],
    );

    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Select
          ref={ref}
          id={id}
          className="min-w-0 flex-1"
          options={groups}
          value={value}
          onChange={onChange}
          placeholder={labels.placeholder}
          disabled={disabled}
          error={error}
          searchable
          searchPlaceholder={labels.search}
          noResultsText={labels.noResults}
          filterOption={filterOption}
          disablePortal={disablePortal}
          renderOption={(option, selected) => {
            const fund = byNumber.get(option.value);
            return (
              <>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{fund?.name ?? option.label}</span>
                  {fund && (
                    <span className="truncate text-xs text-muted-foreground">
                      {[
                        fund.number,
                        fund.canton,
                        `${fund.postalCode} ${fund.city}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                </span>
                {selected && (
                  <Check className="size-4 shrink-0 text-riso-teal" />
                )}
              </>
            );
          }}
          renderValue={(option) => {
            const fund = byNumber.get(option.value);
            return fund ? compensationFundLabel(fund) : option.label;
          }}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={labels.missingHint}
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none"
            >
              <Info className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {renderMissingHint ? renderMissingHint() : labels.missingHint}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  },
);
CompensationFundSelect.displayName = 'CompensationFundSelect';

export { CompensationFundSelect };
